<template>
  <div class="kt-portlet kt-portlet--height-fluid pt-2">
    <AttachCustomForm
      v-model="dialogs.attachCustomForm"
      :internetShop="internetShop"
    ></AttachCustomForm>
    <div
      class="kt-portlet__head kt-portlet__head kt-portlet__head--lg kt-portlet__head--noborder kt-portlet__head--breaker-sm flex-wrap"
    >
      <div class="kt-portlet__head-label mb-2">
        <span class="kt-portlet__head-icon">
          <i class="fal fa-tasks-alt" />
        </span>
        <h3 class="kt-portlet__head-title">Manage Internet Shops</h3>
      </div>
      <div class="kt-portlet__head-toolbar">
        <div class="kt-portlet__head-group">
          <v-btn
            color="primary"
            dark
            class="primaryMainText--text mb-2 ml-2"
            to="/secretshop-management/internet-shop"
            target="_blank"
          >
            <i class="fal fa-plus mr-2" />
            New Internet Shop
          </v-btn>
        </div>
      </div>
    </div>
    <div class="kt-portlet__body">
      <v-data-table
        :headers="headers"
        :items="shops"
        :server-items-length="pagination.total"
        :items-per-page="pagination.per_page"
        :loading="isFetchingData"
        sort-by="dealers"
        class="elevation-1"
        :options.sync="options"
      >
        <template v-slot:item.created_at="{ item }">
          <span>{{ item.created_at | displayDateTime }}</span>
        </template>
        <template v-slot:top>
          <div class="px-3">
            <div class="row no-gutters pt-3">
              <div class="col-xl-4">
                <v-menu
                  ref="rangeMenu"
                  v-model="dateRange.rangeMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      class="pt-0"
                      v-model="dateRangeText"
                      :value="dateRangeText"
                      label="Filter Date Range"
                      prepend-icon="fal fa-calendar"
                      v-on="on"
                      clearable
                      @click:clear="clearDateRangeSelection"
                      clear-icon="fal fa-times-circle"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="dateRange.dates"
                    range
                    no-title
                    color="blue"
                    value="mm-dd-YYYY"
                  >
                    <v-spacer />
                    <v-btn text color="info" @click="cancelRangeSelection"
                      >Cancel</v-btn
                    >
                    <v-btn text color="info" @click="confirmRangeSelection"
                      >OK</v-btn
                    >
                  </v-date-picker>
                </v-menu>
              </div>
              <v-spacer />
              <div class="col-xl-4">
                <v-text-field
                  class="mt-0 pt-0"
                  v-model.trim="filters.search"
                  @keydown="fetchSearch"
                  append-icon="fal fa-search"
                  label="Search"
                  single-line
                  hide-details
                />
              </div>
            </div>
          </div>
        </template>
        <template v-slot:item.action="{ item }">
          <div v-if="item.deleted_at === null">
            <v-btn text @click="attachCustomForm(item)" color="blue darken-3" small v-if="isAllowedToAccess">
              <v-icon small>fab fa-wpforms</v-icon>
            </v-btn>
            <v-btn
              text
              color="blue darken-3"
              small
              v-if="item.dealer_id === 48"
              @click="copySecuredLink(item)"
            >
              <v-icon small>fas fa-clipboard</v-icon>
            </v-btn>
            <v-btn text @click="openShop(item)" color="blue darken-3" small>
              <v-icon small>fal fa-eye</v-icon>
            </v-btn>
            <v-btn text @click="editShop(item)" color="grey darken-3" small>
              <v-icon small>fal fa-edit</v-icon>
            </v-btn>
            <v-btn text @click="deleteItem(item)" color="red darken-3" small>
              <v-icon small>fal fa-trash</v-icon>
            </v-btn>
          </div>
          <div v-else>
            <button
              v-if="item.deleted_at !== null"
              type="button"
              class="btn btn-label-dark btn-sm btn-icon"
              @click="restoreItem(item)"
            >
              <i class="far fa-trash-restore" />
            </button>
          </div>
        </template>
        <template v-slot:no-data>
          <v-btn color="primary" @click="resetTable">Reset</v-btn>
        </template>
      </v-data-table>
    </div>
    <confirm-dialog
      :show-dialog="showConfirmDeleteDialog"
      :confirm-action="proceedToDeletion"
      :close-dialog="closeConfirmDeleteDialog"
      :loading="isDeleting"
    >
      Are you sure you want to proceed?
    </confirm-dialog>
  </div>
</template>
<script>
import {
  filter as _filter,
  find as _find,
  forEach as _forEach,
  head as _head,
  map as _map,
  size as _size,
  debounce as _debounce,
  cloneDeep as _cloneDeep,
} from 'lodash-es';
import draggable from 'vuedraggable';
import moment from 'moment';
import dayjs from 'dayjs';
import ConfirmDialog from '@/components/dialogs/ConfirmDialog';
import AttachCustomForm from '../internet-shop-custom-forms/AttachCustomForm.vue';

function resetToDefaultValues() {
  return {
    dealer: {
      is_dealer: true,
      id: '',
      name: '',
      timezone: '',
      competitor: '',
      zipcode: '',
    },
    location: {
      state: '',
      city: '',
    },
    shop: {
      is_new: '',
      sourceLink: '',
      salesPersonName: '',
      csmName: '',
      callGuideLink: '',
      startAt: '',
      duration: '',
      secretShopperId: null,
    },
    lead: {
      source: 0,
      name: '',
      email: '',
      phoneNumber: '',
    },
    car: {
      vin: '',
      make: '',
      model: '',
      stockNo: '',
    },
    call: {
      first: {
        received: null,
        response: '',
      },
      attempts: 0,
    },
    sms: {
      first: {
        received: null,
        response: '',
      },
      attempts: 0,
    },
    email: {
      first: {
        received: null,
        response: '',
      },
      second: {
        received: null,
        response: '',
      },
      attempts: 0,
    },
    chat: {
      first: {
        received: null,
        response: '',
      },
      attempts: 0,
    },
  };
}

export default {
  name: 'InternetShop',
  components: {
    ConfirmDialog,
    draggable,
    AttachCustomForm
},
  data: () => ({
    internetShop: null,
    dialogs: {
      attachCustomForm: false,
    },
    processingData: false,
    isFetchingRecords: false,
    emailThumb: [],
    chatThumb: [],
    filters: {
      search: '',
      status: '',
    },
    preview: {
      api: '/api/image?height=400&width=400&fileName=',
      chat_screenshots: [],
      email_screenshots: [],
    },
    temp: {
      emailScreenshots: [],
      chat_screenshots: [],
    },
    isFetchingData: false,
    emailScreenshotsPreview: [],
    chatScreenshotsPreview: [],
    pagination: {
      total: 1,
      per_page: 5,
      current_page: 1,
      sortBy: '',
      sortDesc: false,
    },
    options: {},
    numberDefault: [
      {
        text: '+15033869622',
        value: '+15033869622',
      },
      {
        text: 'Working Phone 1',
        value: '+16824782690',
      },
      {
        text: '+15037414894',
        value: '+15037414894',
      },
      {
        text: '+15037147677',
        value: '+15037147677',
      },
    ],
    vinLoading: false,
    twilioLoading: false,
    form: new FormData(),
    files: '',
    verificationScreenshot: [],

    emailScreenshot: [
      {
        name: 'FileRepeater',
        value: {},
      },
    ],
  chatScreenshot: [
      {
        name: 'FileRepeater',
        value: {},
      },
    ],
    openedItem: '',
    internetShopForm: false,
    internetShopSearch: '',
    headers: [
      {
        text: 'Company',
        align: 'left',
        sortable: true,
        value: 'dealer.name',
      },
      {
        text: 'Specific Dealer',
        sortable: true,
        value: 'specific_dealer.name',
      },
      {
        text: 'Secret Shopper',
        value: 'posted_by.name',
        sortable: true,
      },
      {
        text: 'Date Created',
        value: 'created_at',
        sortable: true,
      },
      // {
      //   text: 'Shop Type',
      //   value: 'type',
      //   sortable: true,
      // },
      {
        text: 'Category',
        value: 'category',
        sortable: true,
      },
      {
        text: 'Actions',
        value: 'action',
        align: 'center',
        width: 300,
        sortable: false,
      },
    ],
    editedIndex: -1,
    editedItem: {
      dealer: {
        is_dealer: true,
        id: '',
        name: '',
        timezone: '',
        competitor: '',
        zipcode: '',
      },
      location: {
        state: '',
        city: '',
      },
      shop: {
        is_new: '',
        sourceLink: '',
        salesPersonName: '',
        csmName: '',
        callGuideLink: '',
        startAt: '',
        duration: '',
        secretShopperId: null,
      },
      lead: {
        source: 0,
        name: '',
        email: '',
        phoneNumber: '',
      },
      car: {
        vin: '',
        make: '',
        model: '',
        stockNo: '',
      },
      call: {
        first: {
          received: '',
          response: '',
        },
        attempts: 0,
      },
      sms: {
        first: {
          received: '',
          response: '',
        },
        attempts: 0,
      },
      email: {
        first: {
          received: '',
          response: '',
        },
        second: {
          received: '',
          response: '',
        },
        attempts: 0,
      },
      chat: {
        first: {
          received: '',
          response: '',
        },
        attempts: 0,
      },
    },
    defaultItem: resetToDefaultValues(),
    citiesList: [],
    hourMask: '##:##:##',
    phoneNumbersSearchResult: [],
    aws_url: '',
    showConfirmDeleteDialog: false,
    isDeleting: false,
    selectedItem: {},
    dateRange: {
      rangeMenu: false,
      dateRange: '',
      dates: [],
    },
  }),
  created() {
    this.toggleAllFilter();
    this.fetchData();
    this.$store.dispatch('leadsources/getLeadSource');
    this.$http.get('venv/aws-url').then((response) => {
      this.aws_url = response.data.aws_url;
    });
  },
  mounted() {
    this.$validator.localize('en', {
      attributes: {
        'dealer.is_dealer': 'dealer type',
        'dealer.id': 'dealer group',
        'dealer.name': 'specific dealer',
        'dealer.zipcode': 'Zip Code',
        'dealer.timezone': 'dealer timezone',
        'location.state': 'state',
        'location.city': 'city',
        'shop.is_new': 'shop type',
        'lead.source': 'lead source',
        'shop.sourceLink': 'shop source link',
        'car.vin': 'vehicle identification number',
        'car.make': 'make of the car',
        'car.model': 'model of the car',
        'car.stockNo': 'stock no of the car',
        'lead.name': 'lead name',
        'lead.email': 'lead email',
        'lead.phoneNumber': 'lead phone number',
        'shop.salesPersonName': "sales person's name",
        'shop.csmName': 'csm name',
        'shop.startAt': 'shop start date and time',
        'shop.duration': 'shop duration',
        verificationScreenshot: 'verification screenshot',
      },
    });
  },
  computed: {
    dateRangeText() {
      if (!this.dateRange.dates) {
        return '';
      }
      return this.dateRange.dates.join(' ~ ');
    },
    formTitle() {
      return this.editedIndex === -1
        ? 'New Internet Shop'
        : 'Edit Internet Shop';
    },
    states() {
      return this.$store.state.states.states;
    },
    leadsources() {
      return this.$store.state.leadsources.leadsources;
    },
    cities() {
      return this.$store.state.cities.cities;
    },
    dealers() {
      return this.$store.state.dealers.dealers;
    },
    secretShopperList() {
      return this.$store.state.secretShoppers.secretShoppers;
    },
    shops() {
      return this.$store.state.internetShops.internetShops.data;
    },
    numberList() {
      return _map(
        this.$store.state.phoneNumbers.phoneNumbers.data,
        (number) => number.value
      );
    },
    specificDealersResult() {
      if (this.editedIndex == -1) {
        this.clearSelectedSpecificDealer();
        this.clearSelectedCompetitor();
      }

      if (!this.editedItem.dealer.id) {
        return [];
      }

      const dealerId = this.editedItem.dealer.id;
      const dealers = _filter(this.dealers, ['id', dealerId]);

      if (_head(dealers) == undefined) {
        return [];
      }

      const dealer = _head(dealers);
      const specificDealers = dealer.specific_dealers;

      return specificDealers;
    },
    competitorsResult() {
      if (
        !this.editedItem.dealer.name ||
        isNaN(parseInt(this.editedItem.dealer.name))
      ) {
        return [];
      }

      const specificDealerId = this.editedItem.dealer.name;
      const specificDealers = _filter(this.specificDealersResult, [
        'id',
        specificDealerId,
      ]);

      if (_head(specificDealers) == undefined) {
        return [];
      }

      const specificDealer = _head(specificDealers);
      const { competitors } = specificDealer;

      return competitors;
    },
    isCompetitor: {
      get() {
        return !this.editedItem.dealer.is_dealer;
      },
      set() {
        this.editedItem.dealer.is_dealer = !this.editedItem.dealer.is_dealer;
      },
    },
    isAllowedToAccess() {
      if (window.location.origin.toString().includes('app.webinarinc.com')
        || window.location.origin.toString().includes('stagingv2.webinarinc.com')
        || window.location.origin.toString().includes('localhost')
        || window.location.origin.toString().includes('127.0.0.1')) {
        return true;
      }

      return false;
    },
  },
  watch: {
    internetShopForm(val) {
      // eslint-disable-next-line no-unused-expressions
      val || this.close();
    },
    options: {
      handler(opts) {
        this.pagination.current_page = opts.page;
        this.fetchData();
      },
      deep: true,
    },
  },
  methods: {
    attachCustomForm(item) {
      this.internetShop = item;
      this.dialogs.attachCustomForm = true;
    },
    copySecuredLink({ guest_view_id }) {
      const { hostname } = window.location;

      const link = `${hostname}/guest/internet-shop/${guest_view_id}`;

      this.$copyText(link).then(
        () => {
          this.$notify('success', 'Link copied!');
        },
        () => {
          this.$notify('error', 'Woops something went wrong...');
        }
      );
    },
    cancelRangeSelection() {
      this.dateRange.dates = [];
      this.$refs.rangeMenu.save(null);
      this.dateRange.rangeMenu = false;
      this.pagination.current_page = 1;
      this.fetchData();
    },
    confirmRangeSelection() {
      this.$refs.rangeMenu.save(this.dateRange.dates);
      this.rangeMenu = false;
      this.pagination.current_page = 1;
      this.fetchData();
    },
    clearDateRangeSelection() {
      this.dateRange.rangeMenu = false;
      this.dateRange.dates = [];
      this.$refs.rangeMenu.save(null);
    },
    fetchSearch: _debounce(function () {
      this.fetchData();
    }, 1000),
    currentUser() {
      return this.$auth.user();
    },
    userRoles() {
      return this.currentUser.roles;
    },
    isAdmin() {
      return _some(this.roles, { name: 'super-administrator' });
    },
    openShop(item) {
      let id = item.is_obfuscate
        ? this.isAdmin
          ? item.id
          : item.guest_view_id
        : item.id;
      const route = this.$router.resolve({
        path: `/client/internet-shop/${id}`,
      });
      window.open(route.href, '_blank');
    },
    editShop(item) {
      let id = item.is_obfuscate
        ? this.isAdmin
          ? item.id
          : item.guest_view_id
        : item.id;
      const route = this.$router.resolve({
        path: `/secretshop-management/internet-shop/${id}`,
      });
      window.open(route.href, '_blank');
    },
    clearSelectedSpecificDealer() {
      this.editedItem.dealer.name = '';
    },
    clearSelectedCompetitor() {
      this.editedItem.dealer.competitor = '';
    },
    menuProps() {},
    blurItem(event) {
      document.getElementsByClassName('v-list')[0].style.display = 'none';
    },
    focusItem() {
      document.getElementsByClassName('v-list')[0].style.display = 'block';
    },
    toggleAllFilter() {
      this.filters.status = ['Active', 'Inactive'];
    },
    emailOrderChanged(evt) {
      this.emailThumb.forEach((item, index) => {
        item.order = index;
      });
    },
    emailScreenshotBlur(evt) {
      // console.info(evt.target.files);
      let i = 0;
      // this.emailThumb = [];
      // console.log(evt.target.files.length);
      console.log(evt);
      Array.from(evt.target.files).forEach((file) => {
        const input = file;
        // Ensure that you have a file before attempting to read it

        // create a new FileReader to read this image and convert to base64 format
        const reader = new FileReader();

        const vm = this;
        // Define a callback function to run, when FileReader finishes its job
        reader.onload = (e) => {
          // Note: arrow function used here, so that "this.imageData" refers to the imageData of Vue component
          // Read image as base64 and set to imageData
          const tempData = {};
          tempData.img64 = e.target.result;
          tempData.name = file.name;
          tempData.order = vm.emailThumb.length;
          this.emailThumb.push(tempData);
        };
        // Start the reader job - read file as a data url (base64 format)
        reader.readAsDataURL(input);
        i++;
      });
      this.preview.email_screenshots = null;
    },
    secondsToHMS(duration) {
      if (!duration) return '';
      const seconds = duration / 1000;
      const minutes = seconds / 60;
      let hours = minutes / 60;
      const days = hours / 24;

      if (days > 1) hours = days * 24 + (hours % 24);
      else hours %= 24;
      return `${Math.floor(hours)}:${Math.floor(minutes % 60)}:${Math.floor(
        seconds % 60
      )}`;
    },
    dataURLtoFile(dataurl, filename) {
      const arr = dataurl.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }

      return new File([u8arr], filename, { type: mime });
    },
    calculateCallResponseTime(inputDate) {
      if (this.editedItem.shop.startAt && this.editedItem.call.first.received) {
        const start = moment(this.editedItem.shop.startAt);
        const end = moment(this.editedItem.call.first.received);
        this.editedItem.call.first.response = this.secondsToHMS(
          end.diff(start)
        );
      } else {
        this.editedItem.call.first.response = '';
      }

      if (this.editedItem.shop.startAt && this.editedItem.sms.first.received) {
        const start = moment(this.editedItem.shop.startAt);
        const end = moment(this.editedItem.sms.first.received);
        this.editedItem.sms.first.response = this.secondsToHMS(end.diff(start));
      } else {
        this.editedItem.sms.first.response = '';
      }

      if (
        this.editedItem.shop.startAt &&
        this.editedItem.email.first.received
      ) {
        const start = moment(this.editedItem.shop.startAt);
        const end = moment(this.editedItem.email.first.received);
        this.editedItem.email.first.response = this.secondsToHMS(
          end.diff(start)
        );
      } else {
        this.editedItem.email.first.response = '';
      }

      if (this.editedItem.shop.startAt && this.editedItem.chat.first.received) {
        const start = moment(this.editedItem.shop.startAt);
        const end = moment(this.editedItem.chat.first.received);
        this.editedItem.chat.first.response = this.secondsToHMS(
          end.diff(start)
        );
      } else {
        this.editedItem.chat.first.response = '';
      }
    },
    removeImage(index, screenshots, thumb) {
      thumb.splice(index, 1);
      screenshots.splice(index, 1);
    },
    fetchData() {
      this.pagination.sortBy =
        this.options.sortBy !== undefined ? this.options.sortBy[0] : '';
      this.pagination.sortDesc =
        this.options.sortDesc !== undefined ? this.options.sortDesc[0] : '';

      this.isFetchingRecords = true;
      this.isFetchingData = true;

      const pageSettings = _cloneDeep(this.pagination);
      pageSettings.between_at = this.dateRange.dates;

      this.$store
        .dispatch('internetShops/getInternetShops', {
          pagination: pageSettings,
          filters: this.filters,
        })
        .then(({ data }) => {
          this.$store.commit('internetShops/setInternetShops', data);

          this.pagination.total = data.total;
          // this.pagination.current_page = data.current_page;
          this.pagination.per_page = Number(data.per_page);
        })
        // eslint-disable-next-line no-return-assign
        .finally(() => {
          this.isFetchingRecords = false;
          this.isFetchingData = false;
        });
    },
    changePage(page) {
      this.pagination.current_page = page;
      this.triggerMethod = 'changepage';
      // this.fetchData();
    },
    fetchTwilioData() {
      this.editedItem.sms.attempts = '';
      this.editedItem.sms.first.received = '';
      this.editedItem.sms.first.response = '';

      this.editedItem.call.attempts = '';
      this.editedItem.call.first.received = '';
      this.editedItem.call.first.response = '';

      this.editedItem.email.attempts = '';
      this.editedItem.email.first.received = '';
      this.editedItem.email.first.response = '';

      this.editedItem.chat.attempts = '';
      this.editedItem.chat.first.received = '';
      this.editedItem.chat.first.response = '';

      this.$notify('success', 'Fetching data from twilio...');
      this.twilioLoading = true;
      this.$http
        .post('/twilio/get-specific-logs-by-number', {
          number: this.editedItem.lead.phoneNumber,
          call: true,
          sms: true,
          timezone: this.editedItem.dealer.timezone
            ? this.editedItem.dealer.timezone
            : 'EST',
        })
        .then(({ data }) => {
          if (data.sms) {
            const { sms } = data;
            this.editedItem.sms.attempts = sms.attempts;
            this.editedItem.sms.first.received = new Date(sms.firstReceived);
          }
          if (data.call) {
            const { call } = data;
            this.editedItem.call.attempts = call.attempts;
            this.editedItem.call.first.received = new Date(call.firstReceived);
          }
        })
        .catch((error) => {
          this.twilioLoading = false;
          this.$notify(
            'danger',
            'Something went wrong in fetching the Twilio data.'
          );
        })
        .finally(() => {
          this.twilioLoading = false;
          this.$notify(
            'success',
            'Please check the fields. If there are no value(s) it means that the the data is not existing at the moment.'
          );
        });
    },
    fetchMakeAndModel() {
      this.editedItem.car.make = '';
      this.editedItem.car.model = '';

      if (this.editedIndex === -1) {
        this.$validator
          .validate('car.vin', this.editedItem.car.vin, 'car.vin')
          .then((result) => {
            if (result) {
              this.vinLoading = true;
              this.hitVinApi();
            }
          });
      }
    },
    hitVinApi() {
      this.vinLoading = true;

      this.$http
        .post('/vin', { vin: this.editedItem.car.vin })
        .then(({ data }) => {
          this.editedItem.car.make = data.make;
          this.editedItem.car.model = data.model;
          this.$notify(
            'success',
            'The make and model input field has been automatically updated. Please double check it...'
          );
        })
        .catch(() => {
          this.$notify(
            'error',
            `Cant't find a make and model for this vin ${this.editedItem.car.vin}`
          );
        })
        .finally(() => {
          this.vinLoading = false;
        });
    },
    fetchTwilioSmsData() {
      this.$http
        .get(
          `/secret-shop-management/internet-shops/fetch-sms/${this.editedIndex}`
        )
        .then((response) => {
          alert('Fetched! Please reload');
        });
    },
    setCities(id) {
      this.$http.get(`/cities/${id}`).then(({ data }) => {
        this.$store.dispatch('cities/setCities', data);
      });
    },
    editItem(item) {
      const vm = this;

      this.openedItem = this.shops.indexOf(item);
      this.editedIndex = item.id;
      if (item.state) {
        this.setCities(item.state.id);
      }
      this.editedItem.dealer.id = item.dealer_id;
      this.editedItem.dealer.zipcode = item.zipcode;
      this.editedItem.dealer.timezone = item.timezone;
      this.editedItem.dealer.name = item.specific_dealer.name;

      const competitor = _find(item.specific_dealer.competitors, {
        id: item.competitor_id,
      });
      this.editedItem.dealer.competitor =
        competitor != undefined && competitor.name != undefined
          ? competitor.name
          : '';
      this.editedItem.dealer.timezone = item.timezone;
      this.editedItem.dealer.is_dealer = item.is_dealer === 1;

      if (item.state) {
        this.editedItem.location.state = item.state.id;
      }
      if (item.city) {
        this.editedItem.location.city = item.city.id;
      }
      this.editedItem.shop.is_new = String(item.is_shop_new);
      this.editedItem.shop.csmName = item.csm_name;
      this.editedItem.shop.sourceLink = item.source_link;
      this.editedItem.shop.salesPersonName = item.salesperson_name;
      this.editedItem.shop.secretShopperId = item.user_id;
      this.editedItem.shop.callGuideLink = item.call_guide_link;
      this.editedItem.shop.duration = item.shop_duration;
      this.editedItem.shop.startAt =
        item.start_at != '' ? this.formatDate(item.start_at) : '';

      this.editedItem.car.vin = item.vehicle_identification_number;
      this.editedItem.car.make = item.make;
      this.editedItem.car.model = item.model;
      this.editedItem.car.stockNo = item.stockNo;
      this.editedItem.verification_screenshot = item.verification_screenshot;

      this.editedItem.lead.name = item.lead_name;
      this.editedItem.lead.source =
        item.lead_source !== null ? parseInt(item.lead_source) : null;

      this.editedItem.lead.phoneNumber = item.lead_phone_number;
      this.editedItem.lead.email = item.lead_email;
      if (item.call_first_received) {
        this.editedItem.call.first.received = this.formatDate(
          item.call_first_received
        );
      } else if (vm.$refs.firstCallReceived !== undefined) {
        vm.$refs.firstCallReceived.clearHandler();
      }

      this.editedItem.call.first.response = item.call_response_time;
      this.editedItem.call.attempts = item.call_attempts;

      if (item.sms_first_received) {
        this.editedItem.sms.first.received = this.formatDate(
          item.sms_first_received
        );
      } else if (vm.$refs.firstSmsReceived !== undefined) {
        vm.$refs.firstSmsReceived.clearHandler();
      }
      this.editedItem.sms.first.response = item.sms_response_time;
      this.editedItem.sms.attempts = item.sms_attempts;
      if (item.email_first_received) {
        this.editedItem.email.first.received = this.formatDate(
          item.email_first_received
        );
      } else if (vm.$refs.firstEmailReceived !== undefined) {
        vm.$refs.firstEmailReceived.clearHandler();
      }

      this.editedItem.email.first.response = item.email_response_time;
      this.editedItem.email.attempts = item.email_attempts;

      this.editedItem.chat.first.response = item.chat_response_time;
      this.editedItem.chat.attempts = item.chat_attempts;

      // para sa edit item ma clear mga laman ng screenshots
      this.emailThumb = [];

      // eto ung para sa update nang masama existing uploaded images..
      // comment ka muna ngayon
      // this.temp.emailScreenshots = [];

      _forEach(item.email_screenshots, (screenshot) => {
        this.emailThumb.push({
          id: screenshot.id,
          webp: `https://webinarinc-v2-development.s3-us-west-2.amazonaws.com/${item.id}/email/${screenshot.value}`,
          jpg: `https://webinarinc-v2-development.s3-us-west-2.amazonaws.com/${item.id}/email/${screenshot.fall_back}`,
          order: screenshot.order_by,
          old: true,
        });
      });

      this.internetShopForm = true;
    },
    formatDate(date) {
      if (date) {
        return new Date(date);
      }
      return null;
    },
    closeConfirmDeleteDialog() {
      this.showConfirmDeleteDialog = false;
      this.isDeleting = false;
      this.selectedItem = {};
    },
    proceedToDeletion() {
      this.isDeleting = true;
      const index = this.shops.indexOf(this.selectedItem);
      this.$store
        .dispatch('internetShops/deleteInternetShop', this.selectedItem.id)
        .then(() => {
          this.fetchData();
          this.$notify('success', 'Internet Shop record removed!');
          this.closeConfirmDeleteDialog();
        });
    },
    deleteItem(item) {
      this.selectedItem = item;
      this.showConfirmDeleteDialog = true;
    },
    restoreItem(item) {
      const { id } = item;
      this.$store
        .dispatch('internetShops/restoreInternetShop', id)
        .then(({ data }) => {
          this.$notify('success', 'Internet Shop item successfully restored!');
          this.fetchData();
        });
    },
    checkIfNull(fields) {
      return _size(_filter(fields, { active: true })) >= 1;
    },
    mapFields(fields, name) {
      const files = _map(fields);

      _forEach(files, (val) => {
        this.form.append(name, val);
      });
    },
    setFormData() {
      const { shop, call, email, sms } = JSON.parse(
        JSON.stringify(this.editedItem)
      );
      const dateFormat = 'MM/DD/YYYY hh:mm a';

      shop.startAt = dayjs(shop.startAt).format(dateFormat);

      call.first.received = call.first.received
        ? dayjs(call.first.received).format(dateFormat)
        : null;
      sms.first.received = sms.first.received
        ? dayjs(sms.first.received).format(dateFormat)
        : null;
      email.first.received = email.first.received
        ? dayjs(email.first.received).format(dateFormat)
        : null;
      email.second.received = email.second.received
        ? dayjs(email.second.received).format(dateFormat)
        : null;

      this.form = new FormData();
      this.form.append('dealer', JSON.stringify(this.editedItem.dealer));
      this.form.append('location', JSON.stringify(this.editedItem.location));
      this.form.append('shop', JSON.stringify(shop));
      this.form.append('lead', JSON.stringify(this.editedItem.lead));
      this.form.append('car', JSON.stringify(this.editedItem.car));
      this.form.append('call', JSON.stringify(call));
      this.form.append('sms', JSON.stringify(sms));
      this.form.append('email', JSON.stringify(email));
      this.form.append('chat', JSON.stringify(this.editedItem.chat));

      if (this.emailThumb.length != null) {
        _forEach(_map(this.emailThumb), (screenshot, index) => {
          const vm = this;
          this.form.append(
            `emailScreenshots[${index}][order]`,
            screenshot.order
          );
          if (screenshot.id != undefined) {
            this.form.append(`emailScreenshots[${index}][id]`, screenshot.id);
          } else {
            this.form.append(
              `emailScreenshots[${index}][file]`,
              vm.dataURLtoFile(screenshot.img64, `email ${index}`)
            );
          }
        });
      }

      if (this.verificationScreenshot != null) {
        this.form.append('verificationScreenshot', this.verificationScreenshot);
      }
    },
    store() {
      this.setFormData();
      this.axios
        .post('/secret-shop-management/internet-shops', this.form, {
          headers: {
            'Content-type': 'multipart/form-data',
          },
        })
        // eslint-disable-next-line no-unused-vars
        .then((response) => {
          this.$store.commit(
            'internetShops/insertNewInternetShop',
            response.data
          );
          this.$notify('success', 'Internet shop added!');
          this.close();
        })
        .catch((errors) => {
          console.log(errors);
          const messages = errors.response.data.errors;
          _forEach(messages, (val, key) => {
            this.errors.add({
              field: key,
              msg: val[0],
            });
          });
          this.scrollToFirstError();
        })
        .finally(() => {
          this.processingData = false;
        });
    },
    update() {
      this.setFormData();

      this.form.append('_method', 'PATCH');
      this.form.append('id', this.editedIndex);
      this.axios
        .post(
          `/secret-shop-management/internet-shops/${this.editedIndex}`,
          this.form,
          {
            headers: {
              'Content-type': 'multipart/form-data',
            },
          }
        )
        .then(({ data }) => {
          this.$notify('success', 'Internet shop record updated');
          const index = this.openedItem;
          this.$store.commit('internetShops/editInternetShop', {
            data,
            index,
          });
          // this.shops.splice(index, 1, data);
          this.close();
        })
        .catch((errors) => {
          console.log(errors);
          const messages = errors.response.data.errors;
          _forEach(messages, (val, key) => {
            this.errors.add({
              field: key,
              msg: val[0],
            });
          });
          this.scrollToFirstError();
        })
        .finally(() => {
          this.processingData = false;
        });
    },
    save() {
      this.$validator.validate('*').then((result) => {
        if (result) {
          this.processingData = true;
          if (this.editedIndex > -1) {
            this.update();
          } else {
            this.store();
          }
        } else {
          this.scrollToFirstError();
        }
      });
    },
    scrollToFirstError() {
      if (document.querySelector('.validate-error:first-of-type') !== null) {
        const el = document.querySelector(
          '.validate-error:first-of-type'
        ).parentNode;
        el.scrollIntoView();
      }
    },
    close() {
      const vm = this;
      vm.$refs.startDateAndTime.clearHandler();
      vm.$refs.firstCallReceived.clearHandler();
      vm.$refs.firstSmsReceived.clearHandler();
      vm.$refs.firstEmailReceived.clearHandler();
      // vm.$refs.secondEmailReceived.clearHandler();
      // vm.$refs.firstChatReceived.clearHandler();
      this.internetShopForm = false;
      this.$nextTick(() => {
        this.$validator.reset();
        this.editedIndex = -1;
        this.editedItem = Object.assign({}, resetToDefaultValues());
        this.verificationScreenshot = [];
        this.chatThumb = [];
        this.emailThumb = [];
        this.temp.emailScreenshots = [];
        this.temp.chat_screenshots = [];
      });
    },
    pluckDealerId(selectedOption, id) {
      this.editedItem.dealer.id = selectedOption.id;
    },
    searchPhoneNumber(search, loading) {
      this.$http
        .get('/secret-shop-management/phone-numbers/search', {
          params: {
            search,
          },
        })
        .then((response) => {
          console.log(response);
          this.phoneNumbersSearchResult = response.data.numbers;
        });
    },
    checkSpecificDealerDuplication(val) {
      if (isNaN(val)) {
        const partialMatches = _filter(this.specificDealersResult, (dealer) =>
          dealer.name.includes(val)
        );
        if (partialMatches.length > 0) {
          this.$swal({
            title: 'Are you sure?',
            text: 'It seems there are partial matches on your search, please double check before creating to avoid duplication',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, continue!',
            cancelButtonText: 'No',
            showCloseButton: true,
          }).then((result) => {
            if (!result.value) {
              this.editedItem.dealer.name = '';
            }
          });
        }
      }
    },
    resetTable() {
      this.filters.search = '';
      this.fetchData();
    },
  },
};
</script>
<style lang="stylus" scoped>
adjustImage {
  width: 400px;
  height: 400px;
}
</style>
